import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Switch,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { delftBlue } from "@/theme/colors";
import { getSwitchStyles } from "@/utils/switchStyles";
import FormInput from "@/components/forms/FormInput";
import { useCreateAdminMutation } from "@/app/features/dashboard/dashboardApiSlice";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// Function to generate a secure random password that
// contains at least one uppercase letter, one lowercase letter,
// one number, and one special character.
const generatePassword = () => {
  const length = 12;
  const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowercase = "abcdefghijklmnopqrstuvwxyz";
  const numbers = "0123456789";
  const special = "!@#$%^&*";
  const allChars = uppercase + lowercase + numbers + special;

  const getRandomChar = (charset) =>
    charset.charAt(Math.floor(Math.random() * charset.length));

  // Ensure at least one character from each required group
  const passwordChars = [
    getRandomChar(uppercase),
    getRandomChar(lowercase),
    getRandomChar(numbers),
    getRandomChar(special),
  ];

  // Fill the rest of the password length with random characters
  for (let i = passwordChars.length; i < length; i++) {
    passwordChars.push(getRandomChar(allChars));
  }

  // Shuffle using Fisher–Yates to avoid predictable order
  for (let i = passwordChars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [passwordChars[i], passwordChars[j]] = [passwordChars[j], passwordChars[i]];
  }

  return passwordChars.join("");
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .email("Must be a valid email address")
    .required("Email is required"),
  username: Yup.string()
    .min(2, "Username must be at least 2 characters")
    .max(50, "Username cannot exceed 50 characters")
    .matches(
      /^[a-zA-Z0-9_]+$/,
      "Username can only contain letters, numbers, and underscores"
    )
    .required("Username is required"),
  fullName: Yup.string()
    .max(100, "Full name cannot exceed 100 characters")
    .optional(),
  phoneNumber: Yup.string()
    .max(10, "Phone number cannot exceed 10 characters")
    .optional(),
  isActive: Yup.boolean(),
});

const AdminFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [createAdmin, { isLoading: isCreating }] = useCreateAdminMutation();
  const [generatedPassword, setGeneratedPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Get companyId from location state (passed from CompaniesPage)
  const companyId = location.state?.companyId;
  const companyName = location.state?.companyName;
  const existingAdmin = location.state?.existingAdmin;
  const hasExistingAdmin = Boolean(existingAdmin);

  // Generate password on component mount
  useEffect(() => {
    setGeneratedPassword(generatePassword());
  }, []);

  const initialValues = {
    email: existingAdmin?.email || "",
    username: existingAdmin?.username || "",
    fullName: existingAdmin?.fullName || "",
    phoneNumber: existingAdmin?.phoneNumber || "",
    isActive:
      typeof existingAdmin?.isActive === "boolean"
        ? existingAdmin.isActive
        : true,
  };

  const handleGenerateNewPassword = () => {
    setGeneratedPassword(generatePassword());
  };

  const handleSubmit = async (values, { setStatus, setSubmitting }) => {
    try {
      if (hasExistingAdmin) {
        setStatus({
          message:
            "This company already has an admin invited. You cannot invite another admin from this form.",
        });
        setSubmitting(false);
        return;
      }

      if (!companyId) {
        setStatus({
          message: "Company ID is required. Please go back and try again.",
        });
        setSubmitting(false);
        return;
      }

      const adminData = {
        email: values.email,
        username: values.username,
        fullName: values.fullName || undefined,
        phoneNumber: values.phoneNumber || undefined,
        companyId: companyId,
        isActive: values.isActive,
        password: generatedPassword, // Send generated password to backend
      };

      await createAdmin(adminData).unwrap();
      navigate("/companies", { state: { refresh: true } });
    } catch (error) {
      console.error("Failed to create admin:", error);
      setStatus({
        message:
          error?.data?.message || "Failed to create admin. Please try again.",
      });
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, isSubmitting, status, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Header */}
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 22, sm: 24, md: 28 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.29,
                    marginBottom: 1,
                  }}
                >
                  {hasExistingAdmin ? "Invited Admin" : "Invite Admin"}
                </Typography>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 13, sm: 14, md: 16 },
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}
                >
                  {hasExistingAdmin
                    ? companyName
                      ? `View the invited admin details for ${companyName}.`
                      : "View the invited admin details for this company."
                    : companyName
                    ? `Onboard a new admin for ${companyName}.`
                    : "Onboard a new admin for the company."}
                </Typography>
              </Box>

              {/* Error Message */}
              {status?.message && (
                <Alert severity="error">{status.message}</Alert>
              )}

              {/* Form Fields */}
              <Stack spacing={2}>
                <FormInput
                  name="email"
                  label="Email *"
                  placeholder="admin@example.com"
                  type="email"
                />
                <FormInput
                  name="username"
                  label="Username *"
                  placeholder="admin_username"
                />
                <FormInput
                  name="fullName"
                  label="Full Name"
                  placeholder="John Doe"
                />
                <FormInput
                  name="phoneNumber"
                  label="Phone Number"
                  placeholder="04XXXXXXXX"
                />

                {/* Generated Password Display - only for new admins */}
                {!hasExistingAdmin && (
                  <Box
                    sx={{
                      padding: 2,
                      borderRadius: 2,
                      background: delftBlue[50],
                      border: `1px ${delftBlue[200]} solid`,
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Typography
                        sx={{
                          color: delftBlue[500],
                          fontSize: { xs: 14, sm: 15 },
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 600,
                        }}
                      >
                        System Generated Password
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <TextField
                          value={generatedPassword}
                          type={showPassword ? "text" : "password"}
                          disabled
                          fullWidth
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                  sx={{
                                    color: delftBlue[500],
                                  }}
                                >
                                  {showPassword ? (
                                    <VisibilityOffIcon />
                                  ) : (
                                    <VisibilityIcon />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            flex: 1,
                            "& .MuiInputBase-root": {
                              background: "white",
                              fontFamily: "Montserrat, sans-serif",
                            },
                            "& .MuiInputBase-input": {
                              fontFamily: "Montserrat, sans-serif",
                              fontSize: { xs: 14, sm: 15 },
                              color: delftBlue[700],
                            },
                          }}
                        />
                        <Button
                          type="button"
                          variant="outlined"
                          onClick={handleGenerateNewPassword}
                          sx={{
                            borderRadius: 2,
                            borderColor: delftBlue[200],
                            color: delftBlue[500],
                            textTransform: "none",
                            paddingX: 2,
                            paddingY: 1,
                            whiteSpace: "nowrap",
                            fontFamily: "Montserrat, sans-serif",
                            "&:hover": {
                              borderColor: delftBlue[300],
                              background: delftBlue[50],
                            },
                          }}
                        >
                          Generate New
                        </Button>
                      </Box>
                      <Typography
                        sx={{
                          color: delftBlue[400],
                          fontSize: { xs: 12, sm: 13 },
                          fontFamily: "Montserrat, sans-serif",
                          fontStyle: "italic",
                        }}
                      >
                        This password will be sent to the admin via email.
                        Please save it securely.
                      </Typography>
                    </Stack>
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingY: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: delftBlue[500],
                      fontSize: { xs: 14, sm: 15 },
                      fontFamily: "Montserrat, sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    Active Status
                  </Typography>
                  <Switch
                    checked={values.isActive}
                    onChange={(e) =>
                      setFieldValue("isActive", e.target.checked)
                    }
                    sx={getSwitchStyles(false)}
                  />
                </Box>
              </Stack>

              {/* Action Buttons */}
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate("/companies")}
                  disabled={isSubmitting || isCreating}
                  sx={{
                    borderRadius: 2,
                    borderColor: delftBlue[200],
                    color: delftBlue[500],
                    textTransform: "none",
                    paddingX: 3,
                    paddingY: 1,
                    "&:hover": {
                      borderColor: delftBlue[300],
                      background: delftBlue[50],
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || isCreating || hasExistingAdmin}
                  sx={{
                    borderRadius: 2,
                    background: delftBlue[500],
                    textTransform: "none",
                    paddingX: 3,
                    paddingY: 1,
                    "&:hover": {
                      background: delftBlue[600],
                    },
                    "&:disabled": {
                      background: delftBlue[300],
                    },
                  }}
                >
                  {isSubmitting || isCreating ? (
                    <CircularProgress size={20} sx={{ color: "white" }} />
                  ) : hasExistingAdmin ? (
                    "Admin already invited"
                  ) : (
                    "Invite Admin"
                  )}
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default AdminFormPage;
